import React from 'react';
import { Card, CardContent } from '../ui/card';
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend } from 'recharts';

const AnalyticsCharts = ({ analytics, timeRange }) => {
  if (!analytics) {
    return (
      <Card className="h-[500px]">
        <CardContent className="p-4 flex items-center justify-center h-full">
          <p className="text-gray-500">Loading analytics...</p>
        </CardContent>
      </Card>
    );
  }

  const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];

  // Prepare data for charts
  const topPages = analytics.topPages?.slice(0, 5) || [];
  const trafficSources = analytics.trafficSources || [];
  const deviceData = analytics.deviceBreakdown || [];
  const countryData = analytics.countryBreakdown?.slice(0, 5) || [];

  // Format page URLs for display
  const formattedPages = topPages.map(page => ({
    ...page,
    name: page.url.split('/').pop() || 'Home',
    fullUrl: page.url
  }));

  return (
    <Card className="h-[500px]">
      <CardContent className="p-4 h-full overflow-y-auto">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Analytics Overview</h3>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-xs text-blue-700 font-semibold">Bounce Rate</p>
            <p className="text-2xl font-bold text-blue-900">{analytics.bounceRate?.toFixed(1)}%</p>
          </div>
          <div className="bg-purple-50 p-3 rounded-lg">
            <p className="text-xs text-purple-700 font-semibold">Avg Session</p>
            <p className="text-2xl font-bold text-purple-900">{Math.floor(analytics.avgSessionTime || 0)}s</p>
          </div>
        </div>

        {/* Top Pages Chart */}
        {formattedPages.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Top Pages</h4>
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={formattedPages}>
                <XAxis 
                  dataKey="name" 
                  fontSize={10}
                  tick={{ fill: '#6b7280' }}
                />
                <YAxis fontSize={10} tick={{ fill: '#6b7280' }} />
                <Tooltip 
                  contentStyle={{ fontSize: '12px' }}
                  formatter={(value, name, props) => [
                    value,
                    props.payload.fullUrl
                  ]}
                />
                <Bar dataKey="views" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Traffic Sources Pie Chart */}
        {trafficSources.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Traffic Sources</h4>
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie
                  data={trafficSources}
                  dataKey="count"
                  nameKey="source"
                  cx="50%"
                  cy="50%"
                  outerRadius={60}
                  label={(entry) => `${entry.source}: ${entry.count}`}
                  labelStyle={{ fontSize: '10px' }}
                >
                  {trafficSources.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Device Breakdown */}
        {deviceData.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Device Breakdown</h4>
            <div className="space-y-2">
              {deviceData.map((device, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div 
                    className="h-2 rounded-full" 
                    style={{ 
                      width: `${(device.count / analytics.totalVisits) * 100}%`,
                      backgroundColor: COLORS[index % COLORS.length]
                    }}
                  />
                  <span className="text-xs text-gray-600">
                    {device.device}: {device.count} ({((device.count / analytics.totalVisits) * 100).toFixed(1)}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Countries */}
        {countryData.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Top Countries</h4>
            <div className="space-y-2">
              {countryData.map((country, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{country.country}</span>
                  <span className="text-sm font-semibold text-gray-900">{country.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalyticsCharts;